import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Users, Settings, ArrowLeftRight, Activity, Copy } from "lucide-react";
import { useTelegramViewportBottomOffset } from "@/hooks/useTelegramViewportBottomOffset";

export type NavTab =
  | "alerts"
  | "trade"
  | "referrals"
  | "settings"
  | "price-alerts"
  | "copy-trading";

const NAV_ITEMS: { tab: NavTab; label: string; Icon: typeof Bell }[] = [
  { tab: "alerts", label: "Alerts", Icon: Bell },
  { tab: "trade", label: "Trade", Icon: ArrowLeftRight },
  { tab: "copy-trading", label: "Copy", Icon: Copy },
  { tab: "referrals", label: "Referrals", Icon: Users },
  { tab: "price-alerts", label: "Price Alerts", Icon: Activity },
  { tab: "settings", label: "Settings", Icon: Settings },
];

interface ReferralBottomNavProps {
  activeTab?: NavTab;
}

export const ReferralBottomNav: React.FC<ReferralBottomNavProps> = ({
  activeTab: activeTabProp,
}) => {
  const location = useLocation();

  const navigate = useNavigate();

  const onTabChange = (tab: NavTab) => {
    if (tab === "alerts") {
      navigate("/alerts");
    } else if (tab === "trade") {
      navigate("/positions");
    } else if (tab === "settings") {
      navigate("/settings");
    } else if (tab === "price-alerts") {
      navigate("/price-alerts");
    } else if (tab === "copy-trading") {
      navigate("/copy-trading");
    } else {
      navigate("/referrals");
    }
  };

  // Determine active tab from route if not provided
  const getActiveTab = (): NavTab => {
    if (activeTabProp) return activeTabProp;
    if (location.pathname.includes("/alerts")) return "alerts";
    if (location.pathname.includes("/positions") || location.pathname.includes("/statistics")) return "trade";
    if (location.pathname.includes("/settings")) return "settings";
    if (location.pathname.includes("/price-alerts")) return "price-alerts";
    if (location.pathname.includes("/copy-trading")) return "copy-trading";
    return "referrals";
  };

  const activeTab = getActiveTab();
  const viewportBottomOffset = useTelegramViewportBottomOffset();

  const safeBottom =
    "calc(var(--app-safe-bottom, var(--tg-viewport-safe-area-inset-bottom, var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))) + var(--tg-viewport-content-safe-area-inset-bottom, var(--tg-content-safe-area-inset-bottom, 0px)))";

  return (
    <>
      <div
        aria-hidden
        className="shrink-0"
        style={{ height: `calc(4rem + ${safeBottom})` }}
      />
      <nav
        className="fixed left-0 right-0 z-50 rounded-t-xl border-t border-white/10 bg-[rgba(13,18,26,0.85)] backdrop-blur-md"
        style={{
          bottom: `${viewportBottomOffset}px`,
          paddingBottom: safeBottom,
        }}
      >
        <div className="w-full flex items-center justify-around h-16">
          {NAV_ITEMS.map(({ tab, label, Icon }) => (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={`flex-1 flex flex-col items-center justify-center h-full transition-colors ${
                activeTab === tab
                  ? "text-accent"
                  : "text-text-secondary hover:text-text-primary"
              }`}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="text-[11px] whitespace-nowrap">{label}</span>
            </button>
          ))}
        </div>
      </nav>
    </>
  );
};
